'use client';

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useOrderStore } from '@/store/useOrderStore';
import { cn } from '@/lib/utils';
import { calculateMultiItemDiscounts } from '@/lib/price-calculator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, HelpCircle } from 'lucide-react';
import type { Item, OptionGroup } from '@/types';
import SpecLookupPanel from './SpecLookupPanel';
import LinkOrPhotoInput from './LinkOrPhotoInput';
import { useAdminMode } from '@/hooks/useAdminMode';

interface Props {
  onComplete: () => void;
  onSwitchToQuote?: () => void;
  /**
   * 관리자 시뮬레이션 모드(견적관리 페이지 등) — 다음 3개 영역을 숨김:
   * - 배송가능조건 확인 (reject options)
   * - 물품 링크/사진 입력
   * - 물품 추가 / 다음 단계로 버튼
   */
  hideForAdminSim?: boolean;
}

function calculateRangePrice(
  rc: NonNullable<OptionGroup['rangeConfig']>,
  value: number,
): number {
  const { min, max, minPrice, maxPrice } = rc;
  if (max === min) return minPrice;
  return Math.round(minPrice + ((value - min) / (max - min)) * (maxPrice - minPrice));
}

function calculateTotalPrice(item: Item, options: Record<string, unknown>, overrideBasePrice?: number): number {
  let total = overrideBasePrice ?? item.basePrice;

  // Plus (add)
  if (item.plus) {
    const plusValue = options.plusValue;
    if (item.plus.type === 'select' && typeof plusValue === 'string') {
      const idx = item.plus.names.indexOf(plusValue);
      if (idx >= 0) total += item.plus.prices[idx] || 0;
    } else if (
      item.plus.type === 'range' &&
      item.plus.rangeConfigs &&
      item.plus.rangeConfigs.length > 1 &&
      Array.isArray(plusValue)
    ) {
      // 멀티 range: total = ((x-minX)*pp + minX) * ((y-minY)*pp + minY) * base / (minX * minY)
      const values = plusValue as number[];
      const configs = item.plus.rangeConfigs;
      const pp = item.plus.plusRate!;
      const [x, y] = values;
      const [minX, minY] = [configs[0].min, configs[1].min];
      total = Math.round(((x - minX) * pp + minX) * ((y - minY) * pp + minY) * item.basePrice / (minX * minY));
    } else if (
      item.plus.type === 'range' &&
      typeof plusValue === 'number' &&
      item.plus.rangeConfig
    ) {
      total += calculateRangePrice(item.plus.rangeConfig, plusValue);
    } else if (item.plus.type === 'checkbox' && Array.isArray(plusValue)) {
      (plusValue as string[]).forEach((v) => {
        const idx = item.plus!.names.indexOf(v);
        if (idx >= 0) total += item.plus!.prices[idx] || 0;
      });
    }
  }

  // Extra (add / subtract / multiply)
  if (item.extra) {
    const extraValue = options.extraValue;
    if (Array.isArray(extraValue)) {
      // 곱셈 옵션을 먼저 적용한 뒤 가산/감산 적용
      let addSub = 0;
      (extraValue as string[]).forEach((v) => {
        const idx = item.extra!.names.indexOf(v);
        if (idx < 0) return;
        const raw = item.extra!.rawPrices?.[idx] ?? '';
        if (raw.startsWith('*')) {
          const multiplier = parseFloat(raw.slice(1));
          if (!isNaN(multiplier)) total = Math.round(total * multiplier);
        } else {
          addSub += item.extra!.prices[idx] || 0;
        }
      });
      total += addSub;
    }
  }

  return Math.max(0, total);
}

export default function Step1ItemInfo({ onComplete, onSwitchToQuote, hideForAdminSim = false }: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const { itemInfo, setItemInfo, items: orderItems, addItem, removeItem } = useOrderStore();
  const { isAdmin } = useAdminMode();

  useEffect(() => {
    fetch('/api/items')
      .then((res) => res.json())
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // 카테고리 목록 (순서 유지)
  const categories = useMemo(() =>
    items.reduce<string[]>((acc, item) => {
      if (!acc.includes(item.category)) acc.push(item.category);
      return acc;
    }, []),
    [items],
  );

  // 카테고리 자동 선택 제거 — 사용자가 직접 선택하도록 함

  // 선택된 카테고리의 아이템만 필터링
  const filteredItems = selectedCategory
    ? items.filter((item) => item.category === selectedCategory)
    : [];

  const selectedItem = items.find((item) => item.id === itemInfo.itemId);

  // 다물품 할인 계산 (물품 추가/제거 시 자동 업데이트)
  const discountMap = useMemo(
    () => calculateMultiItemDiscounts(orderItems),
    [orderItems],
  );

  const totalDiscount = useMemo(() => {
    let sum = 0;
    orderItems.forEach((item) => {
      const d = discountMap.get(item.uid);
      if (d && d.discountRate > 0) {
        sum += item.basePrice - d.discountedPrice;
      }
    });
    return sum;
  }, [orderItems, discountMap]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setAttempted(false); // 카테고리 변경 시 에러 표시 초기화
    // 카테고리 변경 시 아이템 선택 초기화
    if (selectedItem && selectedItem.category !== category) {
      setItemInfo({
        itemId: null,
        category: null,
        options: {},
        basePrice: 0,
        isCustom: false,
        customData: undefined,
        locationInsideExtra: 0,
        locationParkingDiscount: 0,
      });
    }
  };

  const handleItemSelect = (item: Item) => {
    setAttempted(false); // 다른 아이템 선택 시 에러 표시 초기화
    const defaultOptions: Record<string, unknown> = {};

    // Initialize reject checkboxes all unchecked
    if (item.rejectOptions.length > 0) {
      const rejectChecked: Record<string, boolean> = {};
      item.rejectOptions.forEach((opt) => {
        rejectChecked[opt] = false;
      });
      defaultOptions.rejectChecked = rejectChecked;
    }

    // Initialize range to min value
    if (item.plus?.type === 'range' && item.plus.rangeConfigs && item.plus.rangeConfigs.length > 1) {
      defaultOptions.plusValue = item.plus.rangeConfigs.map(rc => rc.min);
    } else if (item.plus?.type === 'range' && item.plus.rangeConfig) {
      defaultOptions.plusValue = item.plus.rangeConfig.min;
    }

    // Initialize checkbox types as empty arrays
    if (item.extra) defaultOptions.extraValue = [];

    const basePrice = calculateTotalPrice(item, defaultOptions);

    setItemInfo({
      itemId: item.id,
      category: item.name, // 물품명을 category에 저장 (기존 참조 코드 호환)
      options: defaultOptions,
      basePrice,
      isCustom: false,
      customData: undefined,
      locationInsideExtra: item.locationInsideExtra ?? 0,
      locationParkingDiscount: item.locationParkingDiscount ?? 0,
    });
  };

  const updateOptions = useCallback(
    (key: string, value: unknown) => {
      if (!selectedItem) return;
      const newOptions = { ...itemInfo.options, [key]: value };

      // auto_weight/auto_volume: 규격 조회로 설정된 가격을 base로 사용
      let overrideBase: number | undefined;
      if (selectedItem.plus?.type === 'auto_weight' && newOptions.autoWeightResolved) {
        const weightKg = newOptions.autoWeightKg as number;
        overrideBase = Math.max(selectedItem.basePrice, Math.round(weightKg * 600) + 2000);
      } else if (selectedItem.plus?.type === 'auto_volume' && newOptions.autoVolumeResolved) {
        const { autoWidthMm, autoDepthMm, autoHeightMm } = newOptions as Record<string, number>;
        if (autoWidthMm && autoDepthMm && autoHeightMm) {
          const volumeCm3 = (autoWidthMm / 10) * (autoDepthMm / 10) * (autoHeightMm / 10);
          overrideBase = Math.max(selectedItem.basePrice, Math.round(volumeCm3 * 0.06) + 3000);
        }
      }

      const basePrice = calculateTotalPrice(selectedItem, newOptions, overrideBase);
      setItemInfo({ options: newOptions, basePrice });
    },
    [selectedItem, itemInfo.options, setItemInfo],
  );

  // Validation
  const rejectChecked =
    (itemInfo.options.rejectChecked as Record<string, boolean>) || {};

  // #help가 붙은 옵션이 선택되어 있으면 "운반도움 필수" 합성 reject 옵션을 추가한다
  const HELP_REJECT_LABEL = '운반도움 필수';
  const helpRequiredFromOptions = (() => {
    if (!selectedItem) return false;
    // plus(select): plusValue 문자열과 일치하는 옵션의 helpRequired 검사
    if (selectedItem.plus?.type === 'select' && Array.isArray(selectedItem.plus.helpRequired)) {
      const pv = itemInfo.options.plusValue;
      if (typeof pv === 'string') {
        const idx = selectedItem.plus.names.indexOf(pv);
        if (idx >= 0 && selectedItem.plus.helpRequired[idx]) return true;
      }
    }
    // plus(checkbox): plusValue가 배열일 수 있음
    if (selectedItem.plus?.type === 'checkbox' && Array.isArray(selectedItem.plus.helpRequired)) {
      const pv = itemInfo.options.plusValue;
      if (Array.isArray(pv)) {
        for (const v of pv as string[]) {
          const idx = selectedItem.plus.names.indexOf(v);
          if (idx >= 0 && selectedItem.plus.helpRequired[idx]) return true;
        }
      }
    }
    // extra(checkbox): extraValue 배열의 각 항목 검사
    if (selectedItem.extra && Array.isArray(selectedItem.extra.helpRequired)) {
      const ev = itemInfo.options.extraValue;
      if (Array.isArray(ev)) {
        for (const v of ev as string[]) {
          const idx = selectedItem.extra.names.indexOf(v);
          if (idx >= 0 && selectedItem.extra.helpRequired[idx]) return true;
        }
      }
    }
    return false;
  })();

  const effectiveRejectOptions = selectedItem
    ? helpRequiredFromOptions
      ? [...selectedItem.rejectOptions, HELP_REJECT_LABEL]
      : selectedItem.rejectOptions
    : [];

  const allRejectChecked = selectedItem
    ? effectiveRejectOptions.length === 0 ||
      effectiveRejectOptions.every((opt) => rejectChecked[opt] === true)
    : false;

  const plusValid =
    selectedItem?.plus?.type === 'select'
      ? typeof itemInfo.options.plusValue === 'string' &&
        itemInfo.options.plusValue !== ''
      : true;

  // 멀티 range: 두 값 모두 140 이상이면 배송 불가
  const multiRangeOversize = (() => {
    if (!selectedItem?.plus?.rangeConfigs || selectedItem.plus.rangeConfigs.length <= 1) return false;
    if (selectedItem.name === '카페트') return false;
    const vals = itemInfo.options.plusValue;
    if (!Array.isArray(vals)) return false;
    return (vals as number[]).every(v => v >= 140);
  })();

  // auto_weight / auto_volume 아이템은 규격 조회로 값이 확인되어야 유효
  const autoResolved =
    selectedItem?.plus?.type === 'auto_weight'
      ? itemInfo.options.autoWeightResolved === true
      : selectedItem?.plus?.type === 'auto_volume'
        ? itemInfo.options.autoVolumeResolved === true
        : true;

  // auto_weight: 45kg 초과 시 배송 불가
  const autoWeightOverLimit =
    selectedItem?.plus?.type === 'auto_weight' &&
    typeof itemInfo.options.autoWeightKg === 'number' &&
    (itemInfo.options.autoWeightKg as number) > 45;

  // auto_volume: 높이 140cm 초과 시 배송 불가
  const autoVolumeOverHeight =
    selectedItem?.plus?.type === 'auto_volume' &&
    typeof itemInfo.options.autoHeightMm === 'number' &&
    (itemInfo.options.autoHeightMm as number) > 1400;

  const isCurrentItemValid =
    itemInfo.itemId !== null && allRejectChecked && plusValid && !multiRangeOversize && autoResolved && !autoWeightOverLimit && !autoVolumeOverHeight;

  const hasLinkOrPhoto =
    !!itemInfo.customData?.linkUrl || (itemInfo.customData?.imageUrls?.length ?? 0) > 0;

  // 현재 편집 중인 물품이 있다면 그 물품의 옵션·reject 검증이 통과되어야 한다.
  // (이전에 추가된 물품이 있어도 현재 편집 중인 물품이 미완료면 진행 불가 → 운반도움 필수 등 합성 reject가 무시되지 않게)
  const currentIsClean = !itemInfo.itemId || isCurrentItemValid;
  const isValid = currentIsClean && (isCurrentItemValid || orderItems.length > 0) && hasLinkOrPhoto;

  const [attempted, setAttempted] = useState(false);
  // 에러 마커들 — 카테고리·물품·옵션 중 어느 단계에서 막혔는지 단계별로 노출
  const categoryError = attempted && !selectedCategory && orderItems.length === 0;
  const itemError = attempted && !!selectedCategory && !selectedItem && orderItems.length === 0;
  const rejectError = attempted && !!selectedItem && effectiveRejectOptions.length > 0 && !allRejectChecked;
  const plusError = attempted && !!selectedItem && !plusValid;
  const itemDescError = attempted && selectedItem?.name === '소형(균일가)' &&
    !((itemInfo.options.itemDescription as string) ?? '').trim();

  const handleAddItem = () => {
    if (!isAdmin && (!isCurrentItemValid || !hasLinkOrPhoto)) {
      setAttempted(true);
      return;
    }
    if (!itemInfo.itemId) return; // 관리자라도 itemId 없으면 추가 불가
    addItem();
    setAttempted(false);
    // 카테고리 선택 유지, 아이템 선택만 초기화됨 (addItem이 itemInfo를 리셋)
  };

  const handleNextStep = () => {
    if (!isAdmin && !isValid) {
      setAttempted(true);
      return;
    }
    // 현재 편집 중인 물품이 있으면 자동으로 추가 후 진행
    if (isCurrentItemValid) {
      addItem();
    }
    onComplete();
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-500">
          배송할 물품을 선택해 주세요.<br/>
          {/* * 무게 제한: <strong className="text-gray-600">45kg</strong><br/>
          * 길이 제한: <strong className="text-gray-600">2.1m</strong> (+ 눕힐 수 없는 물품은 높이 1.4m) */}
        </p>
      </div>

      <div className="space-y-4">
          {/* 추가된 물품 목록 */}
          {orderItems.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="text-sm font-medium text-gray-700">
                추가된 물품 ({orderItems.length}건)
              </div>
              {orderItems.map((item) => {
                const disc = discountMap.get(item.uid);
                const hasDiscount = disc && disc.discountRate > 0;
                return (
                  <div
                    key={item.uid}
                    className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-gray-200"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">
                        {item.displayName}
                      </span>
                      {hasDiscount ? (
                        <>
                          <span className="text-sm text-gray-400 line-through">
                            {item.basePrice.toLocaleString()}원
                          </span>
                          <span className="text-sm font-semibold text-[#4DA3FF]">
                            {disc.discountedPrice.toLocaleString()}원
                          </span>
                          <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                            {Math.round(disc.discountRate * 100)}%
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-[#4DA3FF]">
                          {item.basePrice.toLocaleString()}원
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.uid)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {/* 다물품 할인 요약 */}
              {totalDiscount > 0 && (
                <div className="flex items-center justify-between bg-red-50 rounded-md px-3 py-2 border border-red-200">
                  <span className="text-sm font-medium text-red-700">다물품 할인</span>
                  <span className="text-sm font-bold text-red-600">
                    -{totalDiscount.toLocaleString()}원
                  </span>
                </div>
              )}
            </div>
          )}

          {/* 카테고리 탭 */}
          {loading ? (
            <div className="grid grid-cols-2 gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div>
                <div className={cn(
                  'flex gap-2 rounded-lg',
                  categoryError && 'p-2 border border-red-500 ring-2 ring-red-200',
                )}>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                      className={cn(
                        'flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all border-2',
                        selectedCategory === cat
                          ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {categoryError && (
                  <p className="text-sm text-red-500 mt-1">카테고리를 선택해주세요</p>
                )}
              </div>

              {/* 아이템 그리드 (행 단위 렌더링 — 선택된 아이템 행 바로 아래에 옵션 표시) */}
              <div className={cn(
                'space-y-2 rounded-lg',
                itemError && 'p-2 border border-red-500 ring-2 ring-red-200',
              )}>
                {(() => {
                  const rows: Item[][] = [];
                  for (let i = 0; i < filteredItems.length; i += 2) {
                    rows.push(filteredItems.slice(i, i + 2));
                  }
                  const selectedIdx = selectedItem
                    ? filteredItems.findIndex((it) => it.id === selectedItem.id)
                    : -1;
                  const selectedRowIdx = selectedIdx >= 0 ? Math.floor(selectedIdx / 2) : -1;

                  return rows.map((row, rowIdx) => (
                    <div key={rowIdx}>
                      <div className="grid grid-cols-2 gap-2">
                        {row.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => handleItemSelect(item)}
                            className={cn(
                              'p-4 rounded-lg border-2 text-center transition-all',
                              itemInfo.itemId === item.id
                                ? 'border-[#1A3A5C] bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300',
                            )}
                          >
                            {item.icon ? (
                              <img src={item.icon} alt={item.name} className="w-12 h-12 mx-auto mb-1 object-contain" />
                            ) : (
                              <span className="text-2xl block mb-1">📦</span>
                            )}
                            <span className="text-sm font-medium">{item.name}</span>
                          </button>
                        ))}
                      </div>

                      {/* 선택된 아이템의 옵션 — 해당 행 바로 아래 */}
                      {rowIdx === selectedRowIdx && selectedItem && (
                        <div className="mt-2 space-y-3">
                          {/* 1. Reject 확인 체크박스 */}
                          {!hideForAdminSim && effectiveRejectOptions.length > 0 && (
                            <div className={cn(
                              'bg-amber-50 border rounded-lg p-4 space-y-3',
                              rejectError ? 'border-red-500 ring-2 ring-red-200' : 'border-amber-200',
                            )}>
                              <div className="text-sm font-semibold text-amber-800">
                                {selectedItem.name === "소형(균일가)"
                                  ? '균일가 해당 여부를 확인해 주세요. 조건에 맞지 않는 물품은 다른 항목에서 선택해 주세요.'
                                  : <>배송 가능 조건을 확인해 주세요. 조건에 맞지 않으면 {onSwitchToQuote ? <button type="button" onClick={onSwitchToQuote} className="text-blue-600 underline">견적 문의</button> : '견적 문의'}를 이용해 주세요.</>}
                              </div>
                              {effectiveRejectOptions.map((opt) => (
                                <label key={opt} className="flex items-center gap-3 cursor-pointer">
                                  <Checkbox
                                    checked={rejectChecked[opt] === true}
                                    onCheckedChange={(checked) => {
                                      updateOptions('rejectChecked', {
                                        ...rejectChecked,
                                        [opt]: checked === true,
                                      });
                                    }}
                                  />
                                  <span className="text-sm text-amber-900">{opt}<span className="text-red-500 ml-1">*</span></span>
                                </label>
                              ))}
                              {rejectError && (
                                <p className="text-sm text-red-500">위 조건을 모두 확인해주세요</p>
                              )}
                            </div>
                          )}

                          {/* 1.5. 균일가 품목 — 물품 설명 텍스트 입력 */}
                          {selectedItem.name === "소형(균일가)" && (
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                              <label className="text-sm font-medium text-gray-700">
                                어떠한 물품인지 간단히 설명해주세요.
                                <span className="text-red-500 ml-1">*</span>
                              </label>
                              <input
                                type="text"
                                placeholder="예: 선반, 수납박스, 테니스라켓 등"
                                value={(itemInfo.options.itemDescription as string) ?? ''}
                                onChange={(e) => updateOptions('itemDescription', e.target.value)}
                                className={cn(
                                  'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2',
                                  itemDescError
                                    ? 'border-red-500 ring-2 ring-red-200 focus:ring-red-300'
                                    : 'border-gray-300 focus:ring-[#1A3A5C]/30 focus:border-[#1A3A5C]',
                                )}
                              />
                              {itemDescError && (
                                <p className="text-sm text-red-500">물품 설명을 입력해주세요</p>
                              )}
                            </div>
                          )}

                          {/* 2. Plus 옵션 */}
                          {selectedItem.plus && (
                            <div className={cn(
                              'rounded-lg p-4 space-y-2',
                              plusError ? 'bg-gray-50 border border-red-500 ring-2 ring-red-200' : 'bg-gray-50',
                            )}>
                              {/* 레이블: 멀티 range가 아닐 때만 상단에 표시 */}
                              {!(selectedItem.plus.type === 'range' && selectedItem.plus.rangeConfigs && selectedItem.plus.rangeConfigs.length > 1) && (
                                <label className="text-sm font-medium text-gray-700">
                                  {selectedItem.plus.type === 'range' && selectedItem.plus.rangeConfig
                                    ? selectedItem.plus.rangeConfig.label
                                    : '사이즈/타입'}
                                  <span className="text-red-500 ml-1">*</span>
                                </label>
                              )}

                              {/* auto_weight / auto_volume: 규격 확인 패널 */}
                              {(selectedItem.plus.type === 'auto_weight' || selectedItem.plus.type === 'auto_volume') && (
                                <>
                                  <SpecLookupPanel
                                    onWeightFound={selectedItem.plus.type === 'auto_weight' ? (weightKg) => {
                                      const calculated = Math.round(weightKg * 600)+2000;
                                      const price = Math.max(selectedItem.basePrice, calculated);
                                      setItemInfo({
                                        basePrice: price,
                                        options: { ...itemInfo.options, autoWeightResolved: true, autoWeightKg: weightKg },
                                      });
                                    } : undefined}
                                    onSpecsFound={selectedItem.plus.type === 'auto_volume' ? (specs) => {
                                      const { width, depth, height } = specs;
                                      if (width != null && depth != null && height != null) {
                                        const volumeCm3 = (width / 10) * (depth / 10) * (height / 10);
                                        const calculated = Math.round(volumeCm3 * 0.06)+3000;
                                        const price = Math.max(selectedItem.basePrice, calculated);
                                        setItemInfo({
                                          basePrice: price,
                                          options: { ...itemInfo.options, autoVolumeResolved: true, autoWidthMm: width, autoDepthMm: depth, autoHeightMm: height },
                                        });
                                      }
                                    } : undefined}
                                  />
                                  {autoWeightOverLimit && (
                                    <p className="text-sm font-medium text-red-600">
                                      운반 가능 무게를 초과합니다. 용달 서비스를 이용하시거나, {onSwitchToQuote ? <button type="button" onClick={onSwitchToQuote} className="text-blue-600 underline">견적 문의</button> : '견적 문의'}를 이용해주세요.
                                    </p>
                                  )}
                                  {autoVolumeOverHeight && (
                                    <p className="text-sm font-medium text-red-600">
                                      운반 가능 높이를 초과합니다. 용달 서비스를 이용하시거나, {onSwitchToQuote ? <button type="button" onClick={onSwitchToQuote} className="text-blue-600 underline">견적 문의</button> : '견적 문의'}를 이용해주세요.
                                    </p>
                                  )}
                                </>
                              )}

                              {(selectedItem.plus.type === 'select' || selectedItem.plus.type === 'auto_volume') && selectedItem.plus.names.filter(n => n).length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {selectedItem.plus.names.map((name, idx) => (
                                    <button
                                      key={name}
                                      onClick={() => updateOptions('plusValue', name)}
                                      className={cn(
                                        'px-3 py-1.5 rounded-full text-sm border transition-all',
                                        itemInfo.options.plusValue === name
                                          ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400',
                                      )}
                                    >
                                      {name}
                                    </button>
                                  ))}
                                </div>
                              )}

                              {/* 멀티 range (rangeConfigs) */}
                              {selectedItem.plus.type === 'range' &&
                                selectedItem.plus.rangeConfigs &&
                                selectedItem.plus.rangeConfigs.length > 1 &&
                                (() => {
                                  const configs = selectedItem.plus!.rangeConfigs!;
                                  const values = (Array.isArray(itemInfo.options.plusValue)
                                    ? itemInfo.options.plusValue
                                    : configs.map(rc => rc.min)) as number[];
                                  return (
                                    <div className="space-y-4">
                                      {configs.map((rc, i) => {
                                        const currentValue = values[i] ?? rc.min;
                                        return (
                                          <div key={i} className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">
                                              {rc.label}
                                              <span className="text-red-500 ml-1">*</span>
                                            </label>
                                            <div className="flex items-center gap-4">
                                              <input
                                                type="range"
                                                min={rc.min}
                                                max={rc.max}
                                                step={10}
                                                value={currentValue}
                                                onChange={(e) => {
                                                  const newValues = [...values];
                                                  newValues[i] = Number(e.target.value);
                                                  updateOptions('plusValue', newValues);
                                                }}
                                                className="flex-1"
                                              />
                                              <div className="flex items-center gap-1">
                                                <input
                                                  type="number"
                                                  min={rc.min}
                                                  max={rc.max}
                                                  step={10}
                                                  value={currentValue}
                                                  onChange={(e) => {
                                                    let val = Number(e.target.value);
                                                    val = Math.max(rc.min, Math.min(rc.max, val));
                                                    const newValues = [...values];
                                                    newValues[i] = val;
                                                    updateOptions('plusValue', newValues);
                                                  }}
                                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                                />
                                                <span className="text-sm text-gray-500">{rc.unit}</span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      {values.every(v => v >= 140) && selectedItem.name !== '카페트' && (
                                        <p className="text-sm font-medium text-red-600">
                                          두 변이 모두 140cm 이상이면 배송이 불가하며, 사다리차와 용달 서비스를 이용해주세요.
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}

                              {/* 싱글 range (rangeConfig) */}
                              {selectedItem.plus.type === 'range' &&
                                selectedItem.plus.rangeConfig &&
                                !(selectedItem.plus.rangeConfigs && selectedItem.plus.rangeConfigs.length > 1) &&
                                (() => {
                                  const rc = selectedItem.plus!.rangeConfig!;
                                  const currentValue =
                                    (itemInfo.options.plusValue as number) ?? rc.min;
                                  const rangePrice = calculateRangePrice(rc, currentValue);
                                  return (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-4">
                                        <input
                                          type="range"
                                          min={rc.min}
                                          max={rc.max}
                                          step={10}
                                          value={currentValue}
                                          onChange={(e) =>
                                            updateOptions('plusValue', Number(e.target.value))
                                          }
                                          className="flex-1"
                                        />
                                        <div className="flex items-center gap-1">
                                          <input
                                            type="number"
                                            min={rc.min}
                                            max={rc.max}
                                            step={10}
                                            value={currentValue}
                                            onChange={(e) => {
                                              let val = Number(e.target.value);
                                              val = Math.max(rc.min, Math.min(rc.max, val));
                                              updateOptions('plusValue', val);
                                            }}
                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                          />
                                          <span className="text-sm text-gray-500">{rc.unit}</span>
                                        </div>
                                      </div>
                                      {rangePrice > 0 && (
                                        <p className="text-xs text-blue-600">
                                          추가 비용: +{rangePrice.toLocaleString()}원
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}

                              {selectedItem.plus.type === 'checkbox' && (
                                <div className="flex flex-wrap gap-3">
                                  {selectedItem.plus.names.map((name, idx) => {
                                    const price = selectedItem.plus!.prices[idx] || 0;
                                    const checked = (
                                      (itemInfo.options.plusValue as string[]) || []
                                    ).includes(name);
                                    return (
                                      <label
                                        key={name}
                                        className="flex items-center gap-2 cursor-pointer"
                                      >
                                        <Checkbox
                                          checked={checked}
                                          onCheckedChange={(c) => {
                                            const current =
                                              (itemInfo.options.plusValue as string[]) || [];
                                            const newVal = c
                                              ? [...current, name]
                                              : current.filter((v) => v !== name);
                                            updateOptions('plusValue', newVal);
                                          }}
                                        />
                                        <span className="text-sm text-gray-700">
                                          {name}
                                          {price > 0 && (
                                            <span className="text-blue-600 ml-1">
                                              (+{price.toLocaleString()})
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {plusError && (
                                <p className="text-sm text-red-500">사이즈/타입을 선택해주세요</p>
                              )}
                              <p className="text-sm gray-800 whitespace-pre-line">
                                {selectedItem.notes?.split('/').map((line, i, arr) => {
                                  const parts = line.split('견적 문의');
                                  if (parts.length === 1) return <Fragment key={i}>{line}{i < arr.length - 1 && '\n'}</Fragment>;
                                  return <Fragment key={i}>{parts.map((part, j) => (
                                    <Fragment key={j}>
                                      {j > 0 && (onSwitchToQuote ? <button type="button" onClick={onSwitchToQuote} className="text-blue-600 underline">견적 문의</button> : '견적 문의')}
                                      {part}
                                    </Fragment>
                                  ))}{i < arr.length - 1 && '\n'}</Fragment>;
                                })}
                              </p>
                            </div>
                          )}

                          {/* 3. Extra 옵션 */}
                          {selectedItem.extra && (
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                              <label className="text-sm font-medium text-gray-700">옵션</label>
                              <div className="flex flex-wrap gap-3">
                                {selectedItem.extra.names.map((name, idx) => {
                                  const raw = selectedItem.extra!.rawPrices?.[idx] ?? '';
                                  const price = selectedItem.extra!.prices[idx] || 0;
                                  const isMultiplier = raw.startsWith('*');
                                  const isNegative = price < 0;
                                  const checked = (
                                    (itemInfo.options.extraValue as string[]) || []
                                  ).includes(name);

                                  let priceLabel: string | null = null;
                                  if (isMultiplier) {
                                    const pct = Math.round((parseFloat(raw.slice(1)) - 1) * 100);
                                    priceLabel = `(${pct}%)`;
                                  } else if (isNegative) {
                                    priceLabel = `(${price.toLocaleString()}원)`;
                                  } else if (price > 0) {
                                    priceLabel = `(+${price.toLocaleString()}원)`;
                                  }

                                  return (
                                    <label
                                      key={name}
                                      className="flex items-center gap-2 cursor-pointer"
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={(c) => {
                                          const current =
                                            (itemInfo.options.extraValue as string[]) || [];
                                          const newVal = c
                                            ? [...current, name]
                                            : current.filter((v) => v !== name);
                                          updateOptions('extraValue', newVal);
                                        }}
                                      />
                                      <span className="text-sm text-gray-700">
                                        {name}
                                        {priceLabel && (
                                          <span className={cn(
                                            'ml-1',
                                            isNegative ? 'text-orange-600' : isMultiplier ? 'text-green-600' : 'text-blue-600',
                                          )}>
                                            {priceLabel}
                                          </span>
                                        )}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* 물품 링크 또는 사진 (필수) */}
                          {!hideForAdminSim && (
                            <LinkOrPhotoInput
                              linkUrl={itemInfo.customData?.linkUrl ?? ''}
                              imageUrls={itemInfo.customData?.imageUrls ?? []}
                              onLinkChange={(value) => {
                                const current = itemInfo.customData ?? { imageUrls: [], linkUrl: '', description: '' };
                                setItemInfo({ customData: { ...current, linkUrl: value } });
                              }}
                              onImagesChange={(urls) => {
                                const current = itemInfo.customData ?? { imageUrls: [], linkUrl: '', description: '' };
                                setItemInfo({ customData: { ...current, imageUrls: urls } });
                              }}
                              attempted={attempted}
                              required
                            />
                          )}

                          {/* 기본가 표시 (인라인) */}
                          {(itemInfo.basePrice > 0 || autoWeightOverLimit || autoVolumeOverHeight) && (
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                              <span className="text-sm text-gray-600">물품 기본가: </span>
                              <span className="font-bold text-lg text-[#1A3A5C]">
                                {autoWeightOverLimit || autoVolumeOverHeight ? '-' : `${itemInfo.basePrice.toLocaleString()}원`}
                              </span>
                            </div>
                          )}

                          {/* 물품 추가 / 다음 단계로 버튼 */}
                          <div className="flex gap-2">
                            <Button
                              onClick={handleAddItem}
                              variant="outline"
                              className={cn(
                                'flex-1',
                                !isAdmin && (!isCurrentItemValid || !hasLinkOrPhoto) && 'opacity-60 hover:bg-transparent',
                              )}
                              size="lg"
                            >
                              물품 추가
                            </Button>
                            {!hideForAdminSim && (
                              <Button
                                onClick={handleNextStep}
                                className={cn(
                                  'flex-1',
                                  !isAdmin && !isValid && 'bg-gray-300 hover:bg-gray-400 text-gray-700',
                                )}
                                size="lg"
                              >
                                다음 단계로
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>
              {itemError && (
                <p className="text-sm text-red-500">물품을 선택해주세요</p>
              )}
            </>
          )}
      </div>

      {/* 플로팅 HELP 버튼 + Dialog */}
      {selectedItem?.help && (
        <>
          <button
            onClick={() => setHelpOpen(true)}
            className="fixed bottom-4 right-4 z-40 flex items-center gap-1.5 bg-[#1A3A5C] text-white px-4 py-2.5 rounded-full shadow-lg hover:bg-[#15304D] transition-colors"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm font-medium">HELP</span>
          </button>
          <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedItem.name} 도움말</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                {selectedItem.help.split('//').map((block, i) => {
                  const trimmed = block.trim();
                  const imgMatch = trimmed.match(/^<<(.+?)>>$/);
                  if (imgMatch) {
                    return (
                      <img
                        key={i}
                        src={`/images/${imgMatch[1]}`}
                        alt="도움말 이미지"
                        className="w-full rounded-lg"
                      />
                    );
                  }
                  return (
                    <p key={i} className="text-sm text-gray-700">{trimmed}</p>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
